/** @jsx jsx */

import React, { useEffect, useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { css, jsx } from "@emotion/core";
import { Layout, Avatar, Typography, Icon, Button, Tag, Tooltip } from "antd";

import useRedirect from "../../helperHooks/useRedirect";
import useEligibilityMetaData from "../../hooks/auth/useEligibilityMetaData";
import useLoanStatus from "../../hooks/auth/useLoanStatus";
import useAuth from "../../hooks/auth/useAuth";
import useNameAndImage from "../../hooks/profile/useNameAndImage";
import useAPI from "../../helperHooks/useAPI";

import { routes } from "../../Routes";

import { fonts, color, fontSizes } from "../../theme/Theme";
import {
  generateHomepageLinksAndIcons,
  GstinStatus,
  NavigationItem,
  layoutConstants as CONSTANTS
} from "./layoutConstants";
import logo from "../../assets/logo.svg";

import { GstinValidityService } from "../../services/gstinValidityService";

interface LayoutProps {
  children: React.ReactNode;
}

const { Sider, Content } = Layout;
const { Text } = Typography;

const AppLayout: React.FC<LayoutProps> = ({ children }: LayoutProps) => {
  // Library hooks.
  const location = useLocation();

  // Local state.
  const [selectedSidebarTab, setSelectedSidebarTab] = useState<number>(2);

  // Custom hooks.
  const redirect = useRedirect();
  const {
    eligibilityMetaData: { gstin, legalName, isGstinInactive, status, error }
  } = useEligibilityMetaData();
  const { logout, loggingOut } = useAuth();
  const { disableBillsPage, updateGstinStatus } = useLoanStatus();
  const { userName, imageUrl } = useNameAndImage(true);
  const [collapsed, setCollapsed] = useState<boolean>(true);

  // memoized computations.
  const HOMEPAGE_LINKS_AND_ICONS = useMemo(
    () => generateHomepageLinksAndIcons(status),
    [status]
  );

  const { callAPI: checkGstinValidity, loading } = useAPI(
    GstinValidityService.checkForValidity
  );

  // Effects
  useEffect(() => {
    const { pathname } = location;
    if (pathname.startsWith(routes.bills.new)) {
      setSelectedSidebarTab(1);
    } else if (pathname.startsWith(routes.eligibility.new)) {
      setSelectedSidebarTab(2);
    } else if (pathname.startsWith(routes.documents.new)) {
      setSelectedSidebarTab(3);
    } else {
      setSelectedSidebarTab(0);
    }
  }, [location]);

  const handleLinkClick = (routeClicked: string) => () => {
    redirect(routeClicked);
  };
  const statusColor = isGstinInactive ? color.red : color.green;
  const gstinStatus = isGstinInactive
    ? GstinStatus.inactive
    : GstinStatus.active;

  const disableLink = (name: string) => {
    // return false;
    if (name === "BILLS") {
      return disableBillsPage;
    }
    return false;
  };

  const expandSider = () => {
    setCollapsed(false);
  };

  const collapseSider = () => {
    setCollapsed(true);
  };

  const linkItem = (item: NavigationItem, index: number) => (
    <Button
      type="link"
      className={`link-item p-3 pr-0 mb-2 d-flex align-items-center ${
        index === selectedSidebarTab ? "selected-link-item" : ""
      }`}
      key={item.name}
      onClick={handleLinkClick(item.route)}
      disabled={disableLink(item.name)}
    >
      <Icon
        component={() => (
          <img
            src={index === selectedSidebarTab ? item.selectedIcon : item.icon}
            alt="Home"
          />
        )}
        className="pr-2"
      />
      <Text>{item.name}</Text>
    </Button>
  );

  return (
    <Layout css={layoutStyles}>
      <Sider
        theme="light"
        className="sider"
        breakpoint="md"
        collapsedWidth="60px" 
        width="200px" 
        collapsible 
        collapsed={collapsed} 
        trigger={null}
        role='presentation'
        onMouseOver={() => expandSider()}
        onMouseOut={() => collapseSider()}
      >
        <section className="sidebar-details p-3 pb-4 d-flex flex-column justify-content-center align-items-center">
          <Link to={routes.home.new}>
            <img className="pb-4" src={logo} alt="JitFin" />
          </Link>
          <Link to={routes.profile.new} title="Profile">
            <Avatar src={imageUrl} size={70} icon="user" />
          </Link>
          <Text className="sidebar-data-item d-inline-block pt-1 ellipsis">
            <Tooltip title={userName} placement="bottom">
              {userName}
            </Tooltip>
          </Text>
          <Text className="sidebar-data-item d-inline-block pt-1 ellipsis">
            <Tooltip title={legalName} placement="bottom">
              {legalName}
            </Tooltip>
          </Text>
          <Text className="sidebar-data-item d-inline-block pt-1">{gstin}</Text>
          <div className="gstin-containerstatus- mt-2">
            <Tag color={statusColor} className="status-tag">
              {gstinStatus}
            </Tag>
            {isGstinInactive && (
              <Icon
                type="sync"
                onClick={() => {
                  checkGstinValidity(gstin, response => {
                    if (response) {
                      updateGstinStatus(!response.isActive);
                    }
                  });
                }}
                spin={loading}
              />
            )}
          </div>
          <Text className="error">{error}</Text>
        </section>
        <nav className="links d-flex flex-column pr-3">
          {HOMEPAGE_LINKS_AND_ICONS.map(
            ({ name, icon, selectedIcon, route, tooltip }, index) => {
              if (disableLink(name)) {
                return (
                  <Tooltip placement="right" key={name} title={tooltip}>
                    {linkItem({ name, icon, selectedIcon, route }, index)}
                  </Tooltip>
                );
              } else {
                return linkItem({ name, icon, selectedIcon, route }, index);
              }
            }
          )}
        </nav>
        <Button
          type="link"
          className={`link-item logout logout-item p-3 pr-0 mb-2 d-flex align-items-center
          ${!status ? " logout-overlay" : ""}`}
          onClick={logout}
          loading={loggingOut}
        >
          <Icon type="logout" className="pr-2" />
          <Text>{CONSTANTS.button.logout}</Text>
        </Button>
      </Sider>
      <Layout className="site-layout">
        <Content className="px-5 pb-3 pt-1">{children}</Content>
      </Layout>
    </Layout>
  );
};

const layoutStyles = css({
  height: "100vh",
  ".sider": {
    boxShadow: `0.25rem 0 0.625rem 0 ${color.boxShadow}`,
    transition: 'all 0.2s ease-in',
    ".ant-avatar, .sidebar-data-item, .gstin-containerstatus, a img, nav, button.logout span":{
      transition: 'all 0.2s ease-in',
    },
    ".ant-avatar":{
      opacity: 1
    },
    ".sidebar-data-item":{
      opacity: 1,
      height: '24px'
    },
    ".gstin-containerstatus-":{
      height: '22px',
      fontSize: '14px'
    },
    ".sidebar-details":{
      height: '256px'
    },
    "a[href='/home']":{
      height: '45px'
    },
    "a img": {
      width: '100%',
    },
    "nav":{
      width: '180px',
      transition: 'all 0.2s ease-in',
      "button":{
        alignItems: 'flex-start !important',
        textAlign: 'left',
        display: 'flex'
      },
      ".anticon": {
        textAlign: 'center',
        minWidth: '20px'
      },
      ".ant-typography": {
        opacity: 1,
        textAlign: 'left',
        overflow: 'hidden'
      }
    },
    "button.logout span":{
      opacity: 1,
      textAlign: 'left'
    }
  },
  ".sider.ant-layout-sider-collapsed": {
    overflow: `hidden`,
    ".ant-avatar":{
      opacity: 0
    },
    ".sidebar-data-item":{
      opacity: 0
    },
    ".gstin-containerstatus-":{
      fontSize: '7px'
    },
    ".sidebar-details":{
      paddingLeft: ".2rem !important",
      paddingRight: ".2rem !important",
    },
    "nav":{
      width: '95px',
      ".ant-typography": {
        opacity: 0
      }
    },
    "button.logout span":{
      opacity: 0
    }
  },
  ".sidebar-details": {
    fontFamily: fonts.bold,
    ".ant-typography": {
      color: color.bold
    }
  },
  ".error": {
    color: color.red,
    textAlign: "right",
    fontSize: fontSizes.error
  },
  ".sidebar-data-item": {
    textAlign: "center"
  },
  ".links": {
    ".ant-typography": {
      color: color.light
    }
  },
  ".logout": {
    color: color.secondary
  },
  ".link-item": {
    width: "100%",
    height: "100%",
    borderRadius: "0 2rem 2rem 0",
    "&:hover, &:focus, &:active": {
      backgroundColor: color.activeBackground
    },
    button: {
      padding: 0
    }
  },
  ".ellipsis": {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: 150 //since sider is 200
  },
  ".logout-item": {
    marginTop: "auto",
    height: "auto"
  },
  ".selected-link-item": {
    backgroundColor: color.activeBackground,
    fontFamily: fonts.bold,
    ".ant-typography": {
      color: color.primary
    }
  },
  ".ant-layout-sider-zero-width-trigger": {
    // For a responsive Sider trigger.
    border: `solid 1px ${color.border}`
  },
  ".ant-layout-sider-children": {
    display: "flex",
    flexDirection: "column"
  },
  ".logout-overlay": {
    zIndex: 1,
    backgroundColor: color.primary,
    color: color.white,
    span: {
      color: color.white
    },
    "&:hover, &:focus, &:active": {
      backgroundColor: color.primary
    }
  }
});

export default AppLayout;
